import { DEFAULT_RESULT_SIZE } from 'lib/workperks/common/constants';
import EntityMapper from '../models/EntityMapper';
import Location from 'lib/workperks/models/Location';
import User from 'lib/workperks/models/User';
import Site from 'lib/workperks/models/Site';
import SiteViewModel from 'lib/workperks/models/SiteViewModel';
import { VenngoRedemptionType } from 'lib/workperks/models/RedemptionOptions';
import Template from 'lib/workperks/models/Template';
import { SearchImpressionDescriptor } from '../models/Analytics';
import Perk, { PerkIdentifier } from 'lib/workperks/models/Perk';

export enum SearchSortType {
    Distance = 'distance',
    Relevance = 'relevance',
    Alphabetical = 'alpha',
    Tier = 'tier',
    RecentlyUpdated = 'date',
  }

export const sortMap = [
    {
        title: 'Relevance',
        value: SearchSortType.Relevance,
    },
    {
        title: 'Distance',
        value: SearchSortType.Distance,
    },
    {
        title: 'Alphabetical',
        value: SearchSortType.Alphabetical,
    },
    {
        title: 'Recently Updated',
        value: SearchSortType.RecentlyUpdated,
    },
];

export interface SearchServiceDelegate {
    perksDidLoad: (perks: Perk[], impressionDescriptor: SearchImpressionDescriptor) => Promise<Perk[] | undefined | void>;
  }

export type SearchServiceOptions = {
    user: User;
    location: Location;
    site?: SiteViewModel | Site;
    resultSize?: number;
    language?: string;
    esSource?: boolean;
};

export type SerializedSearchService = SearchServiceOptions & {
    offset: number;
    lastTotalHits: number;
    template?: Template;
};

export default class SearchService
{
    user: User;
    location!: Location;
    site?: SiteViewModel;
    language = 'en';
    distanceUnit = 'km';

    sort = SearchSortType.Relevance;
    sortDirection = false;

    offset = 0;
    resultSize = DEFAULT_RESULT_SIZE;
    esSource = true;

    tier?: number;
    ids?: Array<PerkIdentifier>;
    hideIds?: Array<PerkIdentifier>;
    banner = false;
    query?: string;
    subcategoryId: string[] = [];
    categoryId: string[] = [];
    distance = 50;
    redemptionType?: VenngoRedemptionType[];
    showNew = false;
    showOnlineOnly = false;
    showExpiringSoon = false;
    giftCardOnly = false;
    keyword = null;
    label = null;
    visibleLocations = false;
    template?: Template;
    random = false;
    account = null;
    protected accountIds: Array<number> = [];

    protected _lastTotalHits = 0;

    protected delegate?: SearchServiceDelegate;
    private _perkMapper?: EntityMapper<any, Perk>;

    constructor({ user, location, resultSize = DEFAULT_RESULT_SIZE, language = 'en', esSource = true }: SearchServiceOptions) {
        this.user = user;
        // this.site = SiteViewModel.fromSite(site!);
        this.setLocation(location);
        this.resultSize = resultSize;
        this.language = language;
        this.esSource = esSource;
    }

    getTotalHits(): number {
        return this._lastTotalHits;
    }

    hasMoreResults(): boolean {
        if (!this._lastTotalHits) {
            return false;
        }

        return this.offset + this.resultSize < this._lastTotalHits;
    }

    setOffset(offset: number) {
        this.offset = offset;
        return this;
    }

    next(): Promise<Perk[]> {
        if (!this.hasMoreResults()) {
            return Promise.reject();
        }
      
        this.offset += this.resultSize;
        return this.search();
    }

    search(query?: string): Promise<Perk[]> {
        if (query) {
            this.setQuery(query);
        }
        return Promise.resolve([]);
    }

    searchWithTemplate(_: Template): Promise<Perk[]> {
        console.warn('Not implemented');
        return Promise.resolve([]);
    }

    setQuery(query: string) {
        this.query = query && query.length > 0 ? query.toLowerCase() : undefined;
        this.impressionDidChange();
        return this;
    }
    
    setSort(sort: SearchSortType) {
        this.sort = sort;
        return this;
    }
    
    setParentCategory(categoryId: string[]) {
        this.categoryId = categoryId;
        return this;
    }

    setSubCategory(subcategoryId: string[]) {
        this.subcategoryId = subcategoryId;
        return this;
    }
    
    setLocation(location) {
        this.location = location;
        this.distance = this.distance || this.location?.range || 50;
        return this;
    }
    
    setDistance(distance) {
        this.distance = +distance;
        return this;
    }
    
    setShowVisibleLocations(visible: boolean) {
        this.visibleLocations = visible;
        return this;
    }

    setShowNew(showNew: boolean) {
        this.showNew = showNew;
        return this;
    }

    setShowOnlineOnly(showOnlineOnly) {
        this.showOnlineOnly = showOnlineOnly;
        return this;
    }

    setShowExpiringSoon(expiringSoon: boolean) {
        this.showExpiringSoon = expiringSoon;
        return this;
    }

    setGiftCardOnly(giftCardOnly: boolean) {
        this.giftCardOnly = giftCardOnly;
        return this;
    }
    
    setIDs(ids) {
        this.ids = ids;
        return this;
    }

    setHideIDs(ids: Array<PerkIdentifier>) {
        this.hideIds = ids;
        return this;
    }
    
    setTemplate(template: Template) {
        this.template = template;
        this.impressionDidChange();
        return this;
    }
    
    setRedemptionType(redemptionType: VenngoRedemptionType[]) {
        this.redemptionType = redemptionType;
        return this;
    }
    
    setBanner(banner: boolean) {
        this.banner = banner;
        return this;
    }

    setRandom(random: boolean) {
        this.random = random;
        return this;
    }

    setESSource(esSource: boolean) {
        this.esSource = esSource;
        return this;
    }

    setResultSize(resultSize: number) {
        this.resultSize = resultSize;
        return this;
    }
    
    setTier(tier: number | undefined) {
        this.tier = tier;
        return this;
    }
    
    setLanguage(language: string) {
        this.language = language;
        return this;
    }

    setAccount(account) {
        this.account = account;
        return this;
    }

    setKeyword(keyword) {
        this.keyword = keyword;
        return this;
    }

    setLabel(label) {
        this.label = label;
        return this;
    }

    setLastHitsTotal(total: number) {
        this._lastTotalHits = total;
    }

    /**
     *  Returns a descriptor object for search impression
     *  analytics. Needed for PerkMappers to inject those
     *  impressions
     */
    impressionDescriptor(): SearchImpressionDescriptor {
        return {
            templateId: this.template?.id,
            query: this.query,
        };
    }

    serialize(): SerializedSearchService {
        return {
          user: this.user,
          location: this.location,
          offset: this.offset,
          lastTotalHits: this._lastTotalHits,
          template: this.template,
        };
    }

    /**
     * Invoked when an impression property changes. Currently
     * only resets the perkMapper object to update itself with a
     * new impressionDescriptor.
     */
    impressionDidChange() {
        this._perkMapper = undefined;
    }


    static unserialize(serializedInstance: SerializedSearchService) {
        const instance = new this(serializedInstance);
        instance.setOffset(serializedInstance.offset);
        instance.setLastHitsTotal(serializedInstance.lastTotalHits);
        if (serializedInstance.template) instance.setTemplate(serializedInstance.template);
    
        return instance;
    }

    protected get perkMapper(): EntityMapper<any, Perk> {
        if (!this._perkMapper) this._perkMapper = this.createPerkMapper();
        return this._perkMapper;
      }
    
      protected createPerkMapper(): EntityMapper<any, Perk> {
        throw Error('createPerkMapper called on abstract SearchService.');
      }
}